import {
  ArrowDown,
  Bookmark,
  Briefcase,
  Building2,
  Calendar,
  Filter,
  Heart,
  MapPin,
  Search,
  Star,
  Trash2,
  Wallet,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  manproQueryKeys,
  ownerOpportunityService,
  type ProviderOpportunityDto,
} from '../../../api/manpro';
import {
  getOpportunityCoverImages,
  getOpportunitySourceLabel,
} from '../../../lib/manproOpportunityMedia';
import { useVendorFavorite } from '../../hooks/useVendorFavorite';

const opportunityImages = [
  'https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&q=80&w=500',
  'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80&w=500',
  'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&q=80&w=500',
  'https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&q=80&w=500',
  'https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&q=80&w=500',
];


const FAVORITE_LIST_PARAMS = {
  limit: 100,
  offset: 1,
};

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

const getDeadlineNote = (value?: string | null) => {
  if (!value) return 'Deadline belum ditentukan';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Deadline belum ditentukan';

  const diffDays = Math.ceil((date.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return 'Berakhir';
  if (diffDays === 0) return 'Hari ini';
  return `${diffDays} hari lagi`;
};

const Favorit = () => {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const { savedVendors: favoriteVendors, isLoading, isError } = useVendorFavorite();

  const savedVendors = useMemo(() => {
    return favoriteVendors.map((fav: any) => {
      const p = fav.provider || {};
      const s = fav.serviceman;
      const u = s?.user;

      let name = p.company_name || p.contact_person_name || 'Vendor Tanpa Nama';
      let title = name;

      let rating = p.avg_rating || 0;
      let reviews = p.rating_count || 0;
      let projects = p.total_service_served || 0;
      let teamSize = 'Tim';

      if (s && u) {
        title = `${u.first_name} ${u.last_name}`;
        name = `${title} (Mandor: ${p.company_name || 'Independen'})`;

        if (s.parsed_metadata) {
          reviews = s.parsed_metadata.review_count || reviews;
          // Extract numbers from "120 Proyek"
          const parsedProj = parseInt(String(s.parsed_metadata.proyek_selesai).replace(/\D/g, ''));
          if (!isNaN(parsedProj)) {
            projects = parsedProj;
          }
          teamSize = s.parsed_metadata.tim || teamSize;
        }
      }

      return {
        id: fav.id,
        providerId: fav.provider_id,
        servicemanId: fav.serviceman_id,
        name: name,
        title: title,
        category: s ? 'Tukang/Arsitek' : 'Vendor Jasa',
        location: p.company_address || 'Alamat tidak tersedia',
        tags: ['Kontraktor'],
        badge: p.is_approved === 1 ? 'Verified' : 'Mitra',
        rating: rating,
        description: p.about || 'Tidak ada deskripsi yang tersedia.',
        reviews: reviews,
        projects: projects,
        teamSize: teamSize,
      };
    });
  }, [favoriteVendors]);

  const favoritesQuery = useQuery({
    queryKey: ['manpro', 'owner', 'opportunities', 'favorites', FAVORITE_LIST_PARAMS],
    queryFn: () => ownerOpportunityService.getFavoriteOwnerOpportunities(FAVORITE_LIST_PARAMS),
  });

  const favorites = favoritesQuery.data?.data ?? [];

  const favoriteRows = useMemo(
    () =>
      favorites.map((item, index) => ({
        id: item.id,
        title: item.title,
        type: item.procurement_type || item.category || 'Peluang Proyek',
        location: item.location || item.address_detail || '-',
        budget: item.budget_range || 'Budget belum ditentukan',
        deadline: getDeadlineNote(item.bid_deadline || item.deadline),
        deadlineDate: formatDate(item.bid_deadline || item.deadline),
        source: getOpportunitySourceLabel(item),
        images: getOpportunityCoverImages(item),
        image: getOpportunityCoverImages(item)[0] || opportunityImages[index % opportunityImages.length],
      })),
    [favorites],
  );

  const filteredItems = useMemo(
    () =>
      favoriteRows.filter((item) =>
        `${item.title} ${item.type} ${item.location} ${item.source}`
          .toLowerCase()
          .includes(query.toLowerCase()),
      ),
    [favoriteRows, query],
  );

  const removeFavoriteMutation = useMutation({
    mutationFn: (id: string) => ownerOpportunityService.removeFavoriteOwnerOpportunity(id),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['manpro', 'owner', 'opportunities', 'favorites'] }),
        queryClient.invalidateQueries({ queryKey: ['manpro', 'owner', 'opportunities'] }),
      ]);
      toast.success('Peluang dihapus dari favorit.');
    },
    onError: () => {
      toast.error('Gagal menghapus favorit.');
    },
  });

  return (
    <div className="space-y-6">
      <section>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Favorit</h1>
        <p className="mt-2 text-sm text-slate-500">
          Simpan peluang proyek dan vendor yang ingin Anda review kembali sebelum mengambil keputusan.
        </p>
      </section>

      <section className="grid gap-4 md:grid-cols-3">
        {[
          [String(favorites.length), 'Total favorit', Heart],
          [String(favorites.length), 'Peluang proyek', Briefcase],
          [String(favoriteVendors.length), 'Vendor favorit', Building2],
        ].map(([value, label, Icon]) => (
          <div key={label as string} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-extrabold text-slate-900">{value as string}</p>
                <p className="mt-1 text-sm font-semibold text-slate-500">{label as string}</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 text-blue-700">
                <Icon size={24} />
              </div>
            </div>
          </div>
        ))}
      </section>

      <section className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_330px]">
        <div className="min-w-0 space-y-4">
          <div className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 lg:grid-cols-[minmax(260px,1fr)_120px_120px]">
            <label className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-500">
              <Search size={17} />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                className="w-full bg-transparent outline-none"
                placeholder="Cari favorit..."
              />
            </label>
            <button className="inline-flex items-center justify-center gap-2 rounded-xl border border-blue-200 px-3 py-2.5 text-sm font-bold text-blue-700" type="button">
              <Filter size={16} />
              Filter
            </button>
            <button className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-bold text-slate-600" type="button">
              Terbaru
              <ArrowDown size={16} />
            </button>
          </div>

          {favoritesQuery.isLoading && (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse grid gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm lg:grid-cols-[160px_minmax(0,1fr)_180px_120px] lg:items-center">
                  <div className="h-36 w-full rounded-xl bg-slate-200 lg:h-28" />
                  <div className="space-y-3">
                    <div className="h-5 w-24 rounded-full bg-slate-200" />
                    <div className="h-6 w-3/4 rounded-lg bg-slate-200" />
                    <div className="grid gap-2 sm:grid-cols-2">
                      <div className="h-5 w-32 rounded-lg bg-slate-200" />
                      <div className="h-5 w-32 rounded-lg bg-slate-200" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="h-5 w-20 rounded-lg bg-slate-200" />
                    <div className="h-5 w-28 rounded-lg bg-slate-200" />
                    <div className="h-4 w-32 rounded-lg bg-slate-200" />
                  </div>
                  <div className="grid gap-2">
                    <div className="h-10 w-full rounded-xl bg-slate-200" />
                    <div className="h-10 w-full rounded-xl bg-slate-200" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {favoritesQuery.isError && (
            <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-red-100 bg-red-50 p-10 text-center">
              <p className="text-sm font-bold text-red-600">Gagal memuat data favorit.</p>
              <button
                onClick={() => favoritesQuery.refetch()}
                className="rounded-xl bg-red-600 px-4 py-2 text-sm font-extrabold text-white transition hover:bg-red-700"
              >
                Coba Lagi
              </button>
            </div>
          )}

          {!favoritesQuery.isLoading && !favoritesQuery.isError && filteredItems.map((item) => (
            <article key={item.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-blue-100 hover:shadow-md">
              <div className="grid gap-4 lg:grid-cols-[160px_minmax(0,1fr)_180px_120px] lg:items-center">
                <img src={item.image} alt={item.title} className="h-36 w-full rounded-xl object-cover lg:h-28" />
                <div className="min-w-0">
                  <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-extrabold text-blue-700">{item.type}</span>
                  <h2 className="mt-3 text-lg font-extrabold text-slate-900">{item.title}</h2>
                  <div className="mt-3 grid gap-2 text-sm text-slate-500 sm:grid-cols-2">
                    <span className="inline-flex items-center gap-2">
                      <MapPin size={16} />
                      {item.location}
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <Wallet size={16} />
                      {item.budget}
                    </span>
                  </div>
                </div>
                <div className="text-sm">
                  <p className="font-extrabold text-slate-800">Deadline</p>
                  <p className="mt-2 flex items-center gap-2 text-red-500">
                    <Calendar size={15} />
                    {item.deadline}
                  </p>
                  <p className="mt-2 text-xs text-slate-500">{item.deadlineDate} · {item.source}</p>
                </div>
                <div className="grid gap-2">
                  <Link
                    to={`/owner/peluang/${item.id}`}
                    className="rounded-xl bg-blue-600 px-4 py-2.5 text-center text-sm font-extrabold text-white"
                  >
                    Lihat Detail
                  </Link>
                  <button
                    onClick={() => removeFavoriteMutation.mutate(item.id)}
                    disabled={removeFavoriteMutation.isPending}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-100 px-4 py-2.5 text-sm font-extrabold text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                    type="button"
                  >
                    <Trash2 size={16} />
                    Hapus
                  </button>
                </div>
              </div>
            </article>
          ))}

          {!favoritesQuery.isLoading && !favoritesQuery.isError && filteredItems.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
              <p className="text-sm font-semibold text-slate-400">Favorit tidak ditemukan.</p>
              <Link
                to="/owner/peluang"
                className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-extrabold text-white transition hover:bg-blue-700"
              >
                Cari Proyek Favorit
              </Link>
            </div>
          )}
        </div>

        <aside className="w-full rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:sticky lg:top-24 lg:w-[330px]">
          <h2 className="font-extrabold text-slate-900">Vendor Favorit</h2>
          <div className="mt-5 space-y-4">
            {isLoading ? (
              [...Array(5)].map((_, index) => (
                <div
                  key={index}
                  className="animate-pulse rounded-2xl border border-slate-100 p-4"
                >
                  <div className="flex items-start gap-3">
                    <div className="h-11 w-11 rounded-full bg-slate-200" />

                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-36 rounded bg-slate-200" />
                      <div className="h-3 w-24 rounded bg-slate-200" />
                      <div className="h-3 w-28 rounded bg-slate-200" />
                    </div>

                    <div className="h-5 w-5 rounded bg-slate-200" />
                  </div>
                </div>
              ))
            ) : isError ? (
              <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-center text-sm font-medium text-red-600">
                Gagal memuat vendor favorit.
              </div>
            ) : savedVendors.length === 0 ? (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                Belum ada vendor favorit.
              </div>
            ) : (
              savedVendors.slice(0, 5).map((vendor) => (
                <Link
                  key={vendor.id}
                  to="/owner/vendor-tersimpan"
                  className="block rounded-2xl border border-slate-100 p-4 transition hover:border-blue-100 hover:bg-blue-50/40"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-blue-50 font-extrabold text-blue-700">
                      {(vendor.title || vendor.name)
                        .split(" ")
                        .slice(0, 2)
                        .map((word: any) => word[0])
                        .join("")
                        .toUpperCase()}
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="truncate font-extrabold text-slate-900">
                        {vendor.name}
                      </p>

                      <p className="mt-1 truncate text-xs text-slate-500">
                        {vendor.category}
                      </p>

                      <div className="mt-2 flex items-center gap-1 text-xs font-bold text-slate-600">
                        <Star
                          size={14}
                          className="fill-amber-400 text-amber-400"
                        />
                        <span>{Number(vendor.rating).toFixed(1)}</span>
                        <span>•</span>
                        <span>{vendor.projects} proyek</span>
                      </div>
                    </div>

                    <Bookmark
                      size={18}
                      className="shrink-0 fill-blue-600 text-blue-600"
                    />
                  </div>
                </Link>
              ))
            )}
          </div>
        </aside>
      </section>
    </div>
  );
};

export default Favorit;
